/* ============================================================================
   KR Maps Press — print renderer.
   Renders the saved draft as SINGLE trim-size pages (297x210mm each) in
   Prodigi's combined-PDF order: page 1 = front cover, then the interior
   spreads split into left/right pages, last page = back cover. Chromium's
   page.pdf() (Browser Rendering in production, puppeteer/headless Chrome in
   dev) turns the DOM into the print file — text/SVG stay vector, photos
   embed at their intrinsic resolution, maps rasterize at the 300dpi bake
   forced via __KRP_PRINT_DPR.

   Bookmaking note: after the cover, the first interior page lands on a
   RECTO (right-hand side). The studio composes facing 2-page spreads, so a
   half-title leaf is inserted after the front cover (pushing each spread's
   left half onto a verso) and an end leaf before the back cover (keeping
   the PDF page count even). Total pages = 2 x spreads + 2.

   Readiness contract for the PDF driver: wait for
   html[data-krpp-ready="1"], then call window.__krpPrintStatus() and abort
   if .errors is non-empty. Warnings (low-res photos, unflattened maps) are
   reported but non-fatal.
   ============================================================================ */
(function () {
  'use strict';
  var KP = window.KRPress;
  if (!KP) { console.error('[KRPressPrint] kr-press-core.js must load first'); return; }

  /* 300dpi over CSS's 96px/in — studio-core's flatSnapshot reads this and
     bakes map canvases at print density (and lifts its size cap). Set
     BEFORE any map mounts. */
  var PRINT_DPR = 300 / 96;
  window.__KRP_PRINT_DPR = PRINT_DPR;

  /* px-per-CSS-px needed for ~200dpi at placed size — photos below this
     effective resolution get a warning (Prodigi wants 300; 250+ is
     indistinguishable in practice; under ~200 risks visible softness) */
  var WARN_DPR = 200 / 96;

  var status = {
    ready: false,
    spreads: 0,
    pages: 0,
    photosUpgraded: 0,
    lowRes: [],       /* [{pid, neededPx, havePx}] */
    steps: { fonts: false, photos: false, maps: false },  /* where a stalled render is stuck */
    warnings: [],
    errors: []
  };
  window.__krpPrintStatus = function () { return status; };

  function el(tag, cls, attrs, kids) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k) || attrs[k] == null) continue;
        if (k === 'text') n.textContent = attrs[k];
        else n.setAttribute(k, attrs[k]);
      }
    }
    (kids || []).forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }

  function markReady() {
    status.ready = true;
    document.documentElement.setAttribute('data-krpp-ready', '1');
    console.log('[KRPressPrint] READY —', status.pages, 'pages,',
      status.lowRes.length, 'low-res,', status.warnings.length, 'warnings,',
      status.errors.length, 'errors');
  }

  function fail(root, msg) {
    status.errors.push(msg);
    root.appendChild(el('div', 'krpp', null, [
      el('div', 'krpp-page', null, [
        el('div', 'krpp-leaf', null, [
          el('span', 'krpp-leaf-eyebrow', { text: 'KR MAPS / PRESS — PRINT RENDERER' }),
          el('div', 'krpp-leaf-title', { text: msg })
        ])
      ])
    ]));
    markReady();   /* ready-with-errors: the driver must check .errors */
  }

  function leafPage(cls, kids) {
    return el('div', 'krpp-page', null, [el('div', 'krpp-leaf' + (cls ? ' ' + cls : ''), null, kids)]);
  }

  function halfTitleLeaf(project) {
    var title = (project.title || 'The Journey').trim();
    var sub = (project.subtitle || '').trim();
    return leafPage('', [
      el('span', 'krpp-leaf-eyebrow', { text: 'KR MAPS / PRESS' }),
      el('div', 'krpp-leaf-title', { text: title }),
      sub ? el('div', 'krpp-leaf-sub', { text: sub }) : null
    ]);
  }

  function endLeaf() {
    return leafPage('krpp-leaf--end', [
      el('span', 'krpp-leaf-eyebrow', { text: 'COMPOSED BY THE JOURNEY ENGINE · EDITED BY YOU' })
    ]);
  }

  /* one 297mm page showing HALF of a freshly rendered spread. Each page
     renders its own spread instance: the DOM renderers are deterministic,
     and canvases/maps can't be cloned. */
  function spreadPage(studio, sp, half, guides) {
    var clip = el('div', 'krpp-clip' + (half === 'right' ? ' krpp-clip--right' : ''));
    clip.appendChild(studio.renderSpread(sp, { guides: false, reasoning: false }));
    var kids = [clip];
    if (guides) {
      kids.push(el('i', 'krpp-safe', { 'aria-hidden': 'true', text: ' ' }));
      kids.push(el('span', 'krpp-safe-tag', { text: '10 MM SAFE' }));
    }
    return el('div', 'krpp-page', null, kids);
  }

  /* Cloudflare's Browser Rendering Chromium only preserves JPEG passthrough
     for a photo painted WITHOUT a crop — object-fit:cover (also true of
     background-image cover and SVG preserveAspectRatio=slice) falls back to
     a lossless raster re-encode, 5-10x larger for photographic content.
     Every photo frame in this book crops to fill, which is why a 38pp real
     book rendered as a 363MB PDF. Fix: crop-to-fill ourselves via canvas
     BEFORE Chromium ever sees the image, so there's nothing left to crop at
     PDF-export time (plain scaling is confirmed safe) — then force
     object-fit:fill so a sub-pixel rounding gap can never re-trigger it. */
  function coverCropRect(naturalW, naturalH, targetW, targetH) {
    var targetAspect = targetW / targetH;
    if (naturalW / naturalH > targetAspect) {
      var sh = naturalH, sw = sh * targetAspect;
      return { sx: (naturalW - sw) / 2, sy: 0, sw: sw, sh: sh };
    }
    var sw2 = naturalW, sh2 = sw2 / targetAspect;
    return { sx: 0, sy: (naturalH - sh2) / 2, sw: sw2, sh: sh2 };
  }

  function canvasToJpegBlob(canvas, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob); else reject(new Error('toBlob returned null (tainted canvas?)'));
      }, 'image/jpeg', quality);
    });
  }

  function recordLowRes(pid, neededPx, havePx) {
    /* one entry per photo at its LARGEST placement (spreads render twice —
       left/right pages — so every img appears 2x) */
    var prior = null;
    for (var li = 0; li < status.lowRes.length; li++) {
      if (status.lowRes[li].pid === pid) { prior = status.lowRes[li]; break; }
    }
    if (!prior) status.lowRes.push({ pid: pid, neededPx: neededPx, havePx: havePx });
    else if (neededPx > prior.neededPx) { prior.neededPx = neededPx; prior.havePx = havePx; }
  }

  /* crops img's CURRENT pixel content to its display box via canvas, then
     swaps the img to that pre-cropped JPEG. Runs regardless of whether the
     full-res upgrade above succeeded — even a preview-tier fallback still
     uses object-fit:cover and would still get rasterized otherwise. On
     failure (a tainted canvas — source without CORS headers) the img just
     keeps what it already had; the caller's catch absorbs the rejection. */
  function recropForPrint(img, pid) {
    if (!img.naturalWidth || !img.naturalHeight) return;
    var rect = img.getBoundingClientRect();
    if (rect.width < 4 || rect.height < 4) return;
    var outW = Math.max(1, Math.round(rect.width * PRINT_DPR));
    var outH = Math.max(1, Math.round(rect.height * PRINT_DPR));
    var crop = coverCropRect(img.naturalWidth, img.naturalHeight, outW, outH);
    if (crop.sw < outW) recordLowRes(pid, outW, Math.round(crop.sw));

    var canvas = document.createElement('canvas');
    canvas.width = outW; canvas.height = outH;
    canvas.getContext('2d').drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, outW, outH);

    return canvasToJpegBlob(canvas, 0.9).then(function (blob) {
      canvas.width = canvas.height = 0;   // free the crop scratch backing immediately
      img.style.objectFit = 'fill';
      return new Promise(function (resolve) {
        /* wait for the cropped blob to LOAD but do NOT force img.decode():
           decoding here pins a full bitmap for EVERY photo in the book at
           once (~200 x several MB) and OOMs the render tab on large books —
           the build-time crash we hit at ~50+ photos. Undecoded-but-loaded
           imgs let page.pdf() decode each page as it rasterizes and release
           it after, so peak memory tracks one page, not the whole book. */
        img.onload = img.onerror = function () { img.onload = img.onerror = null; resolve(); };
        img.src = URL.createObjectURL(blob);
      });
    });
  }

  /* runs `worker` over `items` with at most `limit` in flight at once. A
     book with a few hundred photos crashed the renderer (OOM — "Target
     closed") when every full-res decode + canvas crop fired concurrently
     via a plain Promise.all; bounding concurrency caps peak memory instead
     of it scaling with book size. */
  function runPool(items, worker, limit) {
    return new Promise(function (resolve) {
      var i = 0, active = 0, total = items.length;
      if (!total) { resolve(); return; }
      function settle() { active--; pump(); }
      function pump() {
        if (i >= total && active === 0) { resolve(); return; }
        while (active < limit && i < total) {
          active++;
          worker(items[i++]).then(settle, settle);
        }
      }
      pump();
    });
  }

  /* swap every photo to the full-resolution original, wait for decode, then
     crop-to-fill via canvas so Chromium never has to (see recropForPrint
     above). Records effective-DPI warnings at the size each photo actually
     prints. Bounded concurrency (see runPool) keeps peak memory flat
     regardless of how many photos the book has. */
  function upgradePhotos(host, urls) {
    var imgs = [].slice.call(host.querySelectorAll('img[data-pid]'));
    return runPool(imgs, function (img) {
      var pid = img.getAttribute('data-pid');
      if (!pid) return Promise.resolve();
      return Promise.resolve(urls.full(pid)).then(function (u) {
        /* lazy imgs below the fold never load in a headless print pass
           (nothing scrolls), and decode() on a never-triggered lazy img
           pends forever — force eager for print */
        img.loading = 'eager';
        var target = u || img.getAttribute('src');
        /* crossOrigin is required for the canvas recrop below to read pixel
           data back out (canvas.toBlob returns null on a tainted canvas);
           force a reload whenever it isn't already set this way */
        if (target && (target !== img.getAttribute('src') || img.crossOrigin !== 'anonymous')) {
          img.crossOrigin = 'anonymous';
          img.src = target;
        }
        var decoded = img.decode ? img.decode().catch(function () {}) : Promise.resolve();
        /* one unreachable photo must not hang the whole render */
        var guarded = Promise.race([
          decoded,
          new Promise(function (res) { setTimeout(res, 15000); })
        ]);
        return guarded.then(function () {
          if (img.naturalWidth || !target) return;
          /* crossOrigin=anonymous load failed outright (source has no CORS
             headers) — retry plain so the photo displays at all; it falls
             back to Chromium's raster path for just this one image instead
             of going missing. Cache-bust: some caches key on URL alone and
             would otherwise replay the failed CORS-mode response. */
          img.crossOrigin = null;
          img.src = target + (target.indexOf('?') >= 0 ? '&' : '?') + 'krpcb=1';
          var retry = img.decode ? img.decode().catch(function () {}) : Promise.resolve();
          return Promise.race([retry, new Promise(function (res) { setTimeout(res, 15000); })]);
        }).then(function () {
          return recropForPrint(img, pid);
        }).then(function () {
          status.photosUpgraded++;
          if (status.photosUpgraded % 20 === 0) console.log('[KRPressPrint] photos upgraded: ' + status.photosUpgraded);
        });
      }).catch(function () {
        status.photosUpgraded++;
        /* missing photo entirely: keep whatever's on screen */
      });
    }, 6);
  }

  /* the maps this page is responsible for: mounted ones, excluding maps
     that sit entirely in a page's clipped-away half (data-krpp-off) */
  function liveMaps(host) {
    return [].filter.call(host.querySelectorAll('.krp-map'), function (m) {
      return !m.hasAttribute('data-krpp-off');
    });
  }
  function mapsPending(host) {
    var maps = liveMaps(host), pending = 0;
    for (var i = 0; i < maps.length; i++) {
      var m = maps[i]._map;
      if (!m || !m._krFlat || m._krFlat.style.display === 'none') pending++;
    }
    return pending;
  }
  /* every mounted map must have its flattened print-density bake showing.
     On timeout, a map that NEVER MOUNTED (Leaflet/tiles down) is an ERROR —
     it would print as a blank panel — while mounted-but-unflattened maps
     stay a warning (live tiles print, just at screen density). */
  function waitMaps(host, timeoutMs) {
    if (!liveMaps(host).length) return Promise.resolve();
    var t0 = Date.now();
    return new Promise(function (res) {
      (function poll() {
        if (!mapsPending(host)) { res(); return; }
        if (Date.now() - t0 > timeoutMs) {
          var maps = liveMaps(host), unmounted = 0, unflattened = 0;
          for (var j = 0; j < maps.length; j++) {
            var m = maps[j]._map;
            if (!m) unmounted++;
            else if (!m._krFlat || m._krFlat.style.display === 'none') unflattened++;
          }
          if (unmounted) status.errors.push(unmounted + ' map(s) never mounted — map areas would print blank');
          if (unflattened) status.warnings.push(unflattened + ' map(s) not flattened at print density — live tiles print instead');
          res(); return;
        }
        setTimeout(poll, 250);
      })();
    });
  }

  /* bound a step without losing its result — one stalled fetch must not
     hold the whole render hostage */
  function capped(p, ms, warnMsg) {
    var done = false;
    p = p.then(function () { done = true; });
    return Promise.race([p, new Promise(function (res) {
      setTimeout(function () {
        if (!done) status.warnings.push(warnMsg);
        res();
      }, ms);
    })]);
  }

  /* wait one enrich pass (weather/elevation onto chapter pages) unless the
     saved project already carries it; the engine's fetches self-timeout at
     6s, so a 9.5s guard can only fire after they've genuinely settled */
  function waitEnrich(ctx) {
    var p = ctx.store.project || {};
    if (p.enrichment || !ctx.engine || !ctx.engine.enrich) return Promise.resolve();
    return new Promise(function (res) {
      var done = false;
      function finish() { if (!done) { done = true; ctx.bus.off('studio-changed', onChange); res(); } }
      function onChange(d) { if (d && d.reason === 'enrich') finish(); }
      ctx.bus.on('studio-changed', onChange);
      setTimeout(finish, 9500);
    });
  }

  function strip(host) {
    var s = el('div', 'krpp-strip');
    function render() {
      while (s.firstChild) s.removeChild(s.firstChild);
      s.appendChild(el('span', null, { text: 'KR PRESS · PRINT RENDER' }));
      var b1 = el('b', null, { text: status.pages + ' PAGES / ' + status.spreads + ' SPREADS' });
      s.appendChild(b1);
      if (status.lowRes.length) {
        s.appendChild(el('span', 'krpp-strip-warn', { text: status.lowRes.length + ' PHOTO(S) UNDER 200 DPI AT PRINT SIZE' }));
      }
      status.warnings.forEach(function (w) { s.appendChild(el('span', 'krpp-strip-warn', { text: w.toUpperCase() })); });
      status.errors.forEach(function (w) { s.appendChild(el('span', 'krpp-strip-warn', { text: 'ERROR: ' + w.toUpperCase() })); });
      s.appendChild(el('span', status.ready ? 'krpp-strip-ok' : null, { text: status.ready ? 'READY FOR PDF' : 'PREPARING…' }));
    }
    render();
    host._renderStrip = render;
    return s;
  }

  function mount(root, ctx) {
    document.documentElement.className += ' krpp-doc';
    document.body.className += ' krpp-body';

    if (!window.KRPressStudio || !window.KRPressEngine) { fail(root, 'The studio scripts did not load.'); return; }
    var project = (ctx.store && ctx.store.project) || {};
    if (!project.photos || !project.photos.length) { fail(root, 'No draft in this browser to print.'); return; }

    var guides = /[?&]guides=1/.test(location.search);

    /* the studio must exist BEFORE the enrich wait: create() is what kicks
       the enrichment fetch off, and its maybeEnrich emits exactly one
       'enrich' on every terminal path (cache hit, success, failure) — so
       subscribing after create() is race-free and the 9.5s timer becomes a
       rarely-hit backstop over the engine's own 6s fetch timeouts. Waiting
       BEFORE create() (the original order) listened for an event that
       could not fire yet: it always burned the full timer AND the pages
       baked without weather/elevation. */
    var studio;
    try {
      studio = window.KRPressStudio.create({
        store: ctx.store, config: ctx.config, bus: ctx.bus, urls: ctx.urls, engine: window.KRPressEngine
      });
    } catch (e) {
      fail(root, 'The book failed to compose.'); return;
    }

    waitEnrich(ctx).then(function () {
      var B = studio.BOOK();
      if (!B.length || B[0].type !== 'cover') { fail(root, 'The composed book has no cover spread.'); return; }

      status.spreads = B.length;
      status.pages = 2 * B.length + 2;   /* covers + half-title + end leaf + interiors */
      if (status.pages < 24) {
        /* Prodigi hardcover minimum — the driver's abort-on-errors contract
           catches this; on-screen preview still renders for debugging */
        status.errors.push(status.pages + ' pages — under the 24-page hardcover minimum');
      }

      var host = el('div', 'krpp');
      document.body.appendChild(strip(host));

      /* PDF page order */
      host.appendChild(spreadPage(studio, B[0], 'right', guides));   /* front cover */
      host.appendChild(halfTitleLeaf(project));                      /* recto filler → spreads face */
      for (var i = 1; i < B.length; i++) {
        host.appendChild(spreadPage(studio, B[i], 'left', guides));
        host.appendChild(spreadPage(studio, B[i], 'right', guides));
      }
      host.appendChild(endLeaf());                                   /* keeps the count even */
      host.appendChild(spreadPage(studio, B[0], 'left', guides));    /* back cover */
      root.appendChild(host);

      /* each page renders a FULL spread instance, so a map that sits
         entirely in the clipped-away half is pure overhead (its twin page
         has its own visible instance) — hide those before Leaflet's
         deferred mount ever fires (makeMap polls clientWidth, so
         display:none keeps it unmounted). Halves live map count + memory. */
      [].forEach.call(host.querySelectorAll('.krpp-page'), function (pg) {
        var pr = pg.getBoundingClientRect();
        if (pr.width < 4) return;
        [].forEach.call(pg.querySelectorAll('.krp-map'), function (mEl) {
          var r = mEl.getBoundingClientRect();
          if (r.right <= pr.left + 1 || r.left >= pr.right - 1) {
            mEl.style.display = 'none';
            mEl.setAttribute('data-krpp-off', '1');
          }
        });
      });

      /* flush layout so the print DOM's @font-face fetches start NOW —
         fonts.ready captured before any layout can resolve early with
         fallback glyphs still on the pages */
      void host.offsetHeight;
      var fontsReady = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();

      return Promise.all([
        fontsReady.then(function () { status.steps.fonts = true; }),
        capped(upgradePhotos(host, ctx.urls), 240000, 'photo upgrade timed out — some photos may print at preview resolution')
          .then(function () { status.steps.photos = true; }),
        waitMaps(host, 90000).then(function () { status.steps.maps = true; })
      ]).then(function () {
        /* settle: only flag ready once the map bakes hold steady on two
           consecutive checks (a late re-tile/re-bake after the last poll
           would otherwise be photographed mid-swap) */
        return new Promise(function (res) {
          var stable = 0, t0 = Date.now();
          (function settle() {
            if (!mapsPending(host)) stable++; else stable = 0;
            if (stable >= 2 || Date.now() - t0 > 10000) { res(); return; }
            setTimeout(settle, 350);
          })();
        });
      }).then(function () {
        markReady();
        if (host._renderStrip) host._renderStrip();
      });
    }).catch(function (e) {
      /* any synchronous throw in BOOK()/renderers/DOM build must surface
         as ready-with-errors, not a silent hang the driver times out on */
      fail(root, 'Print render threw: ' + ((e && e.message) || e));
    });
  }

  /* ── remote source: ?job=TOKEN ──────────────────────────────────────
     The Browser Rendering worker never has the customer's IndexedDB — it
     uploads the draft to R2 first, then opens this page with ?job=. The
     manifest carries the EXACT store.project shape plus a photo-id → URL
     map; a synthetic ctx replaces the local store/urls so the same mount()
     renders either source. config.print.jobBase points at the job host
     (the krmaps-api worker in production, /mock-jobs in the dev harness).
     Manifest: { v:1, project:{...}, photos:{ id:{url} } } */
  function jobParam() {
    var m = /[?&]job=([A-Za-z0-9_-]{1,64})/.exec(location.search);
    return m ? m[1] : null;
  }
  function remoteCtx(manifest) {
    var photos = manifest.photos || {};
    function urlOf(id) { var e = photos[id]; return e ? (e.url || e.full || null) : null; }
    var store = {
      project: manifest.project,
      /* in-memory only: enrichment written by the studio (weather/elev)
         must LAND so this render includes it — nothing persists */
      update: function (fn) { try { fn(store.project); } catch (e) {} },
      saveNow: function () { return Promise.resolve(); }
    };
    return {
      config: KP.config,
      bus: KP.bus,
      engine: window.KRPressEngine,
      store: store,
      urls: { thumb: urlOf, mid: urlOf, full: function (id) { return Promise.resolve(urlOf(id)); } }
    };
  }
  /* the render worker may point this page at a different job host (its
     own staging api) via ?jobBase= — allowlisted to KR Maps' own hosts so
     nobody can feed our renderer foreign manifests */
  function jobBase() {
    var m = /[?&]jobBase=([^&]+)/.exec(location.search);
    if (m) {
      var b = decodeURIComponent(m[1]);
      if (/^https:\/\/[a-z0-9-]+\.krmaps\.workers\.dev\//.test(b) || b.indexOf(location.origin + '/') === 0 || b.charAt(0) === '/') {
        return b;
      }
    }
    return (KP.config && KP.config.print && KP.config.print.jobBase) || '';
  }
  function mountRemote(root) {
    var job = jobParam();
    var base = jobBase();
    if (!base) { fail(root, 'No print job host configured.'); return; }
    fetch(base.replace(/\/$/, '') + '/' + job + '/manifest.json', { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('manifest ' + r.status); return r.json(); })
      .then(function (manifest) {
        if (!manifest || !manifest.project) throw new Error('manifest has no project');
        mount(root, remoteCtx(manifest));
      })
      .catch(function (e) { fail(root, 'Print job could not be loaded (' + e.message + ').'); });
  }

  /* The core router picks onboarding/builder/mobile by state — on the
     print page only this module is loaded, so claim every surface. In
     job mode the local draft (if any) is ignored entirely. */
  var mod = jobParam()
    ? { mount: mountRemote, unmount: function () {} }
    : { mount: mount, unmount: function () {} };
  KP.register('onboarding', mod);
  KP.register('builder', mod);
  KP.register('mobile', mod);
})();
